import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { API_URL } from '../common/Constants';
import moment from 'moment';


const StatusCircle = styled('span')(({ status }) => ({
    display: 'inline-block',
    width: 16,
    height: 16,
    borderRadius: '50%',
    marginLeft: 8,
    verticalAlign: 'middle',
    backgroundColor:
        status === 'operational' ? '#4caf50' :
            status === 'degraded' ? '#ffb300' :
                status === 'outage' ? '#ff1744' :
                    status === 'maintenance' ? '#2196f3' : '#9e9e9e',
}));

const statusToChipColor = (s) => {
    switch (s) {
        case 'operational': return 'success';
        case 'degraded': return 'warning';
        case 'outage': return 'error';
        case 'maintenance': return 'info';
        default: return 'default';
    }
};

export default function SystemAlerts() {
    const [loading, setLoading] = useState(true);
    const [system, setSystem] = useState(null);
    const currentDateFormatted = moment().format('MMMM Do, YYYY');

    useEffect(() => {
        let mounted = true;
        axios.get('/system/health')
            .then(res => {
                if (mounted) setSystem(res.data);
            })
            .catch(err => {
                console.error('Failed to load system health', err);
                if (mounted) setSystem(null);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => { mounted = false; };
    }, []);

    // Helper to accept multiple backend shapes: servicesStatusMap, servicesStatus object, or JSON string
    const parseServicesMap = (sys) => {
        if (!sys) return {};
        if (sys.servicesStatusMap && typeof sys.servicesStatusMap === 'object') return sys.servicesStatusMap;
        if (sys.servicesStatus && typeof sys.servicesStatus === 'object') return sys.servicesStatus;
        if (sys.servicesStatus && typeof sys.servicesStatus === 'string') {
            try { return JSON.parse(sys.servicesStatus); } catch (e) { return {}; }
        }
        return {};
    };

    const servicesMap = parseServicesMap(system);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px' }}>
                <Typography variant="h5" component="h1" >
                    System Status
                </Typography>
            </div>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Centered Operational Status */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                            <Typography variant="h4" sx={{ textTransform: 'capitalize', fontWeight: 700, textAlign: 'center', pr: 1 }}>
                                {system?.systemStatus || 'unknown'}
                            </Typography>
                            <StatusCircle status={system?.systemStatus} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {`Last updated: ${currentDateFormatted}`}
                        </Typography>
                    </Box>

                    {system?.serviceNotes && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="text.secondary">{system.serviceNotes}</Typography>
                        </Box>
                    )}

                    {/* Table of services */}
                    <Table sx={{ mt: 2, width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(servicesMap).length > 0 ? (
                                Object.entries(servicesMap).map(([name, status]) => (
                                    <TableRow key={name} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                                        <TableCell>{name}</TableCell>
                                        <TableCell>
                                            <Chip label={status} color={statusToChipColor(status)} size="small" />
                                        </TableCell>
                                        <TableCell>{currentDateFormatted}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography color="text.secondary">No service data available.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </>
            )}
        </div>
    );
}
